import type { ReferencedDecisionsInterface } from '@/types/api-types';
import Link from 'next/link';

interface DecisionDetailsProps {
  selectedDecision: ReferencedDecisionsInterface;
  t: (key: string) => string; // Pass translation function as prop
}

/**
 * Component to display the details of a selected decision.
 */
export function DecisionDetails({ selectedDecision, t }: DecisionDetailsProps) {
  // Get court name from selected decision
  const getCourtName = () =>
    selectedDecision?.additional_data?.courts
      .map((court) => court.name)
      .join(', ') || '';

  // Get judge name from selected decision
  const getJudgeName = () => {
    return (
      selectedDecision?.additional_data?.judges
        .map((judge) => judge.name)
        .join(', ') || ''
    );
  };

  // Get nature type from selected decision
  const getNatureType = () => {
    return (
      selectedDecision?.additional_data?.natures
        .map((nature) => nature.name)
        .join(', ') || ''
    );
  };

  return (
    <div className='bg-surface-card p-4 border-b'>
      <h2 className='font-semibold text-base mb-1 text-content-secondary'>
        {t('header.titleForDetails')}
      </h2>
      <h3 className='text-xl font-semibold mb-2'>
        {selectedDecision.decision_form}
      </h3>
      <div className='grid grid-cols-2 gap-x-8 gap-y-1 text-sm'>
        <div>
          <p className='text-content-primary font-bold'>{t('header.court')}:</p>
          <p className='font-medium'>{getCourtName()}</p>
        </div>
        <div>
          <p className='text-content-primary font-bold'>
            {t('header.fileNumber')}:
          </p>
          <Link
            href={`https://www.justice.gov.sk/sudy-a-rozhodnutia/sudy/rozhodnutia/?nazov_name=${selectedDecision.case_file_number}&pageNum=1&size=10&sortProperty=datum_vydania_rozhodnutia&sortDirection=DESC`}
            className='font-medium underline text-brand-primary-color'
          >
            {selectedDecision.case_file_number}
          </Link>
        </div>
        <div>
          <p className='text-content-primary font-bold'>
            {t('header.decisionIssued')}:
          </p>
          <p className='font-medium'>{getJudgeName()}</p>
        </div>
        <div>
          <p className='text-content-primary font-bold'>
            {t('header.fileIdentification')}:
          </p>
          <Link
            href={`https://www.justice.gov.sk/sudy-a-rozhodnutia/sudy/rozhodnutia/?nazov_name=${selectedDecision.identification_number}&pageNum=1&size=10&sortProperty=datum_vydania_rozhodnutia&sortDirection=DESC`}
            className='font-medium underline text-brand-primary-color'
          >
            {selectedDecision.identification_number}
          </Link>
        </div>
        <div>
          <p className='text-content-primary font-bold'>
            {t('header.issueDate')}:
          </p>
          <p className='font-medium'>
            {new Date(selectedDecision.release_date).toLocaleDateString(
              'en-GB'
            )}
          </p>
        </div>
        <div>
          <p className='text-content-primary font-bold'>{t('header.ecli')}:</p>
          <p className='font-medium'>{selectedDecision.ecii}</p>
        </div>
        <div>
          <p className='text-content-primary font-bold'>
            {t('header.natureOfDecision')}:
          </p>
          <p className='font-medium'>{getNatureType()}</p>
        </div>
      </div>
    </div>
  );
}
